import { useAuth } from "@/providers/auth"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { InferRequestType, InferResponseType } from "hono/client"
import { type NavigateFunction, useNavigate } from "react-router"
import { toast } from "sonner"
import { apiClient } from "../../lib/apiClient"

const req = apiClient.auth.login.$post
export type LoginInput = InferRequestType<typeof req>["json"]
export type LoginResponse = InferResponseType<typeof req>

export const useLogin = (redirectPath?: string | null) => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { login } = useAuth()

	return useMutation({
		mutationFn: login,
		onSuccess: async () => {
			void queryClient.invalidateQueries({ queryKey: ["user", "me"] })

			if (redirectPath) {
				let navigatedExternally = false
				try {
					navigatedExternally = await redirectAfterLogin(redirectPath, navigate)
				} catch (error) {
					console.error("Redirect after login failed:", error)
					toast.error(
						"Signed in, but we couldn't complete the redirect. Please try again.",
					)
					return
				}

				if (navigatedExternally) {
					return
				}
			}

			toast.success("Login successful")
		},
	})
}

type RedirectTarget =
	| { kind: "internal"; path: string }
	| { kind: "external"; url: string }

export const redirectAfterLogin = async (
	redirect?: string | null,
	navigate?: NavigateFunction,
): Promise<boolean> => {
	const target = resolveRedirectTarget(redirect)
	if (!target) {
		return false
	}

	if (target.kind === "internal") {
		const currentPath =
			typeof window !== "undefined"
				? `${window.location.pathname}${window.location.search}${window.location.hash}`
				: null

		if (currentPath && target.path === currentPath) {
			return false
		}

		if (navigate) {
			navigate(target.path, { replace: true })
		} else if (typeof window !== "undefined") {
			window.location.assign(target.path)
		}
		return false
	}

	const destination = new URL(target.url)
	const needsAppToken =
		destination.protocol !== "http:" && destination.protocol !== "https:"
			? true
			: destination.hostname === "127.0.0.1" ||
				destination.hostname === "localhost"

	if (needsAppToken) {
		const tokens = await requestAppToken()
		destination.searchParams.set("token", tokens.token)
		destination.searchParams.set(
			"expires_in",
			Math.max(tokens.expiresIn, 0).toString(),
		)
	}

	window.location.href = destination.toString()
	return true
}

const resolveRedirectTarget = (
	redirect?: string | null,
): RedirectTarget | null => {
	if (!redirect || typeof window === "undefined") {
		return null
	}

	const trimmed = redirect.trim()
	if (!trimmed) {
		return null
	}

	if (trimmed.startsWith("/")) {
		return { kind: "internal", path: trimmed }
	}

	try {
		const url = new URL(trimmed, window.location.origin)
		const isHttp = url.protocol === "http:" || url.protocol === "https:"
		const isSameOrigin = isHttp && url.origin === window.location.origin

		if (isSameOrigin) {
			const path = `${url.pathname}${url.search}${url.hash}`
			return { kind: "internal", path }
		}

		return { kind: "external", url: url.toString() }
	} catch {
		return null
	}
}

const requestAppToken = async () => {
	try {
		const response = await apiClient.auth.app.token.$post()

		return await response.json()
	} catch (error) {
		throw new Error(
			error instanceof Error ? error.message : "Failed to parse token",
		)
	}
}
